import { TRUST_BOUNDARY } from "./constants";
import { formatMoney } from "./money";
import type { BuyerFundingFlowSession } from "../flows/buyerFunding/types";
import type { Money } from "./types";

export type PaymentRequestInviteEmail = {
  to: string;
  subject: string;
  greeting: string;
  bodyParagraphs: string[];
  secureLink: string;
  trustNote: string;
};

export function paymentRequestInviteEmail(
  session: Pick<
    BuyerFundingFlowSession,
    "buyerName" | "officeName" | "fileCode" | "paymentRequest"
  > & { amountDue?: Money; amountDueLabel?: string },
  buyerLink: string,
  buyerEmail?: string
): PaymentRequestInviteEmail {
  const amountLabel =
    session.amountDueLabel ?? (session.amountDue ? formatMoney(session.amountDue) : "Amount on file");
  const subject = `You've been invited — secure payment for file ${session.fileCode} (${amountLabel})`;
  return {
    to: buyerEmail?.trim() || session.buyerName,
    subject,
    greeting: `${session.buyerName},`,
    bodyParagraphs: [
      `${session.officeName} invited you to complete ${session.paymentRequest.purpose} for file ${session.fileCode}.`,
      `Amount due: ${amountLabel}.`,
      "Open the secure link below to verify your identity, review wiring instructions, and pay online.",
      "Use only the wire details shown after you sign in. If you receive different instructions by email or text, stop and call the office."
    ],
    secureLink: buyerLink,
    trustNote: TRUST_BOUNDARY
  };
}

const VETO_LOGO_EMAIL_URL = "https://tryveto.com/logo.png";

export function paymentRequestInviteEmailHtml(email: PaymentRequestInviteEmail): string {
  const paragraphs = email.bodyParagraphs.map((p) => `<p style="margin:0 0 12px;line-height:1.5;color:#333">${escapeHtml(p)}</p>`).join("");
  return `<!DOCTYPE html>
<html><body style="font-family:system-ui,sans-serif;max-width:560px;margin:0 auto;padding:24px">
<p style="margin:0 0 20px"><img src="${VETO_LOGO_EMAIL_URL}" alt="Veto" width="160" height="52" style="display:block;height:40px;width:auto" /></p>
<p style="margin:0 0 16px">${escapeHtml(email.greeting)}</p>
${paragraphs}
<p style="margin:24px 0"><a href="${escapeHtml(email.secureLink)}" style="display:inline-block;background:#2563eb;color:#fff;text-decoration:none;padding:12px 20px;border-radius:6px;font-weight:700">Open secure payment link</a></p>
<p style="margin:0 0 8px;font-size:13px;color:#666">Or copy this link:<br><a href="${escapeHtml(email.secureLink)}">${escapeHtml(email.secureLink)}</a></p>
<p style="margin:24px 0 0;font-size:12px;color:#888">${escapeHtml(email.trustNote)}</p>
</body></html>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function paymentRequestInviteEmailPlainText(email: PaymentRequestInviteEmail): string {
  return [
    `Subject: ${email.subject}`,
    "",
    email.greeting,
    "",
    ...email.bodyParagraphs,
    "",
    `Secure link: ${email.secureLink}`,
    "",
    email.trustNote
  ].join("\n");
}

export function paymentRequestPushTitle(officeName: string): string {
  return `${officeName} invited you`;
}

export function paymentRequestPushBody(
  purpose: string,
  amountLabel: string
): string {
  return `You've been invited to pay ${purpose} (${amountLabel}). Use the link in the email to open your secure Veto session — do not wire from instructions received only by text.`;
}

export function paymentRequestInviteSms(
  officeName: string,
  purpose: string,
  amountLabel: string,
  fileCode: string,
  buyerLink: string
): string {
  return `${officeName} invited you to ${purpose} (${amountLabel}) on file ${fileCode}. Open your secure link: ${buyerLink}`;
}
